import { Fragment, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { callMessagingFunction } from '../portals/admin/pages/whatsapp/liveData';

/**
 * The shared pieces for sending an invoice or a payment reminder by WhatsApp from
 * the CleanMyBed number: who it goes to, the message the customer will see, and
 * the send itself (messaging-send send_invoice / send_reminder). Used by the
 * Service Logs board's Send Invoice and Send Reminder windows, the Invoices
 * pop-up and the invoice page, so all of them say and do exactly the same.
 * The invoice is never changed here (no status, amounts, Zoho or payments).
 */

export type WhatsAppInvoiceKind = 'invoice' | 'reminder';

export interface WhatsAppInvoiceData {
  id: string;
  invoiceNumber: string;
  status: string;
  balanceDue: number;
  firstName: string;
  phone: string | null;
}

/** Head office only, the same rule as sending an invoice by email (messaging-send checks it too). */
export function useIsHeadOffice(): boolean {
  const { isSuperAdmin, getScope } = useAuth();
  const scope = getScope();
  return isSuperAdmin || scope === 'global' || scope === 'all';
}

/** The same mobile rule the send function applies, with the country's dialling code. */
function waNumber(raw: string | null | undefined, countryDigits: string): string | null {
  let d = (raw ?? '').replace(/\D/g, '');
  if (d.startsWith('00')) d = d.slice(2);
  if (d.startsWith('0')) d = `${countryDigits}${d.slice(1)}`;
  if (!countryDigits || !d.startsWith(countryDigits)) return null;
  if (countryDigits === '27') return /^27[6-8]\d{8}$/.test(d) ? `+${d}` : null;
  return d.length >= 10 && d.length <= 15 ? `+${d}` : null;
}

/** The same first-name rule as the send function: letters only, else "there". */
function safeFirstName(raw: string | null | undefined): string {
  const first = (raw ?? '').trim().split(/\s+/)[0] ?? '';
  return /^[\p{L}][\p{L}'-]{0,29}$/u.test(first) ? first : 'there';
}

/** Loads what the preview and the send need; reload() after the invoice changes (e.g. marked sent). */
export function useWhatsAppInvoice(invoiceId: string | null | undefined) {
  const [data, setData] = useState<WhatsAppInvoiceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!invoiceId) { setLoading(false); return; }
      setLoading(true);
      const { data: inv } = await supabase.from('invoices')
        .select('id, invoice_number, status, balance_due, customer_id, customer_name')
        .eq('id', invoiceId).maybeSingle();
      if (!inv) { if (!cancelled) { setData(null); setLoading(false); } return; }
      const [{ data: cust }, { data: cfg }] = await Promise.all([
        inv.customer_id
          ? supabase.from('customers').select('first_name, customer_name, mobile, phone').eq('id', inv.customer_id).maybeSingle()
          : Promise.resolve({ data: null }),
        supabase.from('country_config').select('phone_prefix').limit(1).maybeSingle(),
      ]);
      if (cancelled) return;
      const digits = String(cfg?.phone_prefix ?? '').replace(/\D/g, '');
      setData({
        id: inv.id as string,
        invoiceNumber: (inv.invoice_number as string | null) ?? '',
        status: (inv.status as string | null) ?? '',
        balanceDue: Number(inv.balance_due) || 0,
        firstName: safeFirstName(cust?.first_name ?? cust?.customer_name ?? (inv.customer_name as string | null)),
        phone: waNumber(cust?.mobile, digits) ?? waNumber(cust?.phone, digits),
      });
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [invoiceId, version]);

  return { data, loading, reload: () => setVersion((v) => v + 1) };
}

/** Why this invoice cannot go by WhatsApp right now, or null when it can. */
export function whatsAppBlocker(data: WhatsAppInvoiceData | null, allowDraft = false): string | null {
  if (!data) return 'Invoice not found';
  if (data.status === 'void') return 'Void invoices cannot be sent';
  if (data.status === 'draft' && !allowDraft) return 'Send the invoice first';
  if (!(data.balanceDue > 0)) return 'Nothing is owed on this invoice';
  if (!data.phone) return 'No valid mobile number on this customer';
  return null;
}

/** The approved wording (messaging-templates DEFINITIONS), as the customer will see it. */
function messageLines(kind: WhatsAppInvoiceKind, d: WhatsAppInvoiceData, amount: string): string[] {
  return kind === 'invoice'
    ? [
      `Hi ${d.firstName}`, '',
      `Your CleanMyBed SA invoice ${d.invoiceNumber} for ${amount} is ready.`, '',
      'You can pay securely by card or EFT, or view and download the invoice, using the button below.', '',
      'Warm regards,', 'CleanMyBed - Accounts',
    ]
    : [
      `Hi ${d.firstName}`, '',
      `Friendly reminder - your CleanMyBed SA invoice ${d.invoiceNumber} for ${amount} is ready for payment.`, '',
      "If you've already paid, please disregard this message.", '',
      'Warm regards,', 'CleanMyBed - Accounts',
    ];
}

export function WhatsAppInvoicePreview({ kind, data, formatCurrency }: {
  kind: WhatsAppInvoiceKind;
  data: WhatsAppInvoiceData;
  formatCurrency: (amount: number) => string;
}) {
  return (
    <>
      <div className="detail-row">
        <span className="label">To</span>
        <span className="value">{data.phone ?? 'No valid mobile number on this customer'}</span>
      </div>
      <div className="detail-row">
        <span className="label">From</span>
        <span className="value">CleanMyBed WhatsApp (Accounts)</span>
      </div>
      <div className="form-group mt-lg">
        <span className="form-label">Message</span>
        <div className="highlight-box">
          {messageLines(kind, data, formatCurrency(data.balanceDue)).map((line, i) => (
            <Fragment key={i}>{line}<br /></Fragment>
          ))}
          <strong>[ Pay securely ] [ I have a question ] [ I&apos;ve already paid ]</strong>
        </div>
        <span className="form-help">Replies land in the Accounts inbox in CMB Chats and on the WhatsApp page.</span>
      </div>
    </>
  );
}

/** Sends the approved invoice or reminder message. Returns an error text, or null when sent. */
export async function sendWhatsAppInvoice(kind: WhatsAppInvoiceKind, invoiceId: string): Promise<string | null> {
  const { error } = await callMessagingFunction('messaging-send', {
    action: kind === 'invoice' ? 'send_invoice' : 'send_reminder',
    invoice_id: invoiceId,
  });
  return error;
}

/**
 * Runs every server-side refusal check (inbox on, connected, message approved, opted out,
 * just sent, balance, phone) without sending or writing anything. Returns an error text,
 * or null when the send would go. Called before a draft is marked sent, so a refused
 * WhatsApp never leaves an invoice marked sent (and queued to Zoho) with nothing sent.
 */
export async function checkWhatsAppInvoice(kind: WhatsAppInvoiceKind, invoiceId: string): Promise<string | null> {
  const { error } = await callMessagingFunction('messaging-send', {
    action: kind === 'invoice' ? 'send_invoice' : 'send_reminder',
    invoice_id: invoiceId,
    check_only: true,
  });
  return error;
}
